/* oxlint-disable react/only-export-components */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchApi, refreshSession, setAccessToken } from '../api';
import { disconnectSocket, initSocket } from '../socket';

export interface RegisterInput {
  name: string;
  email: string;
  password: string;
  company?: string;
  /** 152-ФЗ: the server refuses registration without it. */
  consent: boolean;
  /** Present when the visitor arrived through an invitation link. */
  inviteToken?: string;
}

interface AuthContextType {
  user: any;
  loadingAuth: boolean;
  setUser: React.Dispatch<React.SetStateAction<any>>;
  login: (email: string, password: string, options?: { inviteToken?: string }) => Promise<any>;
  register: (input: RegisterInput) => Promise<any>;
  logout: () => Promise<void>;
  /** Re-read /auth/me, e.g. right after the email has been confirmed. */
  refreshUser: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loadingAuth: true,
  setUser: () => {},
  login: async () => undefined,
  register: async () => undefined,
  logout: async () => {},
  refreshUser: async () => undefined,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    refreshSession()
      .then((session) => {
        setAccessToken(session.token);
        initSocket(session.token);
        setUser(session.user);
      })
      .catch(() => {
        setAccessToken(null);
        setUser(null);
      })
      .finally(() => setLoadingAuth(false));
    const refreshInterval = window.setInterval(() => {
      refreshSession().catch(() => {
        disconnectSocket();
        setAccessToken(null);
        setUser(null);
      });
    }, 10 * 60 * 1000);
    return () => window.clearInterval(refreshInterval);
  }, []);

  const login = async (email: string, password: string, options?: { inviteToken?: string }) => {
    const data = await fetchApi('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(options?.inviteToken ? { inviteToken: options.inviteToken } : {}) }),
    });
    setAccessToken(data.token);
    initSocket(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (input: RegisterInput) => {
    const data = await fetchApi('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        name: input.name,
        email: input.email,
        password: input.password,
        company: input.company,
        consent: input.consent,
        ...(input.inviteToken ? { inviteToken: input.inviteToken } : {}),
      }),
    });
    setAccessToken(data.token);
    initSocket(data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await fetchApi('/auth/logout', { method: 'POST' });
    } finally {
      disconnectSocket();
      setAccessToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    const data = await fetchApi('/auth/me');
    setUser((current: any) => ({ ...current, ...data }));
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loadingAuth, setUser, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
